import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  CreateDamagePackageWithTypesRequest,
  CreateDamagePackageRequest,
  DamagePackageFilters,
  DamagePackageModel,
  DamagePackageScope,
} from '@app/core/models/product-parameters/damage-package.model';

@Injectable({
  providedIn: 'root',
})
export class DamagePackageService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/damage-packages`;
  }

  getAll(filters: DamagePackageFilters = {}): Observable<DamagePackageModel[]> {
    this.isLoading.set(true);
    let params = new HttpParams();

    if (filters.networkId) {
      params = params.set('networkId', filters.networkId);
    }
    if (filters.active !== undefined && filters.active !== null) {
      params = params.set('active', String(filters.active));
    }

    return this.http.get<DamagePackageModel[]>(this.baseUrl, { params }).pipe(
      catchError(error => {
        console.error('Ошибка при получении пакетов повреждений:', error);
        this.translate.get('Ошибка при получении пакетов повреждений').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /**
   * GET /b2b/damage-packages/by-network/{networkId} | /by-partner/{partnerId} | /all
   */
  getByScope(scope: DamagePackageScope = {}): Observable<DamagePackageModel[]> {
    this.isLoading.set(true);

    const networkId = scope.networkId?.trim();
    const partnerId = scope.partnerId?.trim();
    const url = networkId
      ? `${this.baseUrl}/by-network/${encodeURIComponent(networkId)}`
      : partnerId
        ? `${this.baseUrl}/by-partner/${encodeURIComponent(partnerId)}`
        : `${this.baseUrl}/all`;

    return this.http.get<DamagePackageModel[]>(url).pipe(
      catchError(error => {
        console.error('Ошибка при получении пакетов повреждений:', error);
        this.translate.get('Ошибка при получении пакетов повреждений').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getById(id: string): Observable<DamagePackageModel | null> {
    if (!id) return of(null);
    this.isLoading.set(true);
    return this.http.get<DamagePackageModel>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при получении пакета повреждений:', error);
        this.translate.get('Ошибка при получении пакета повреждений').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  create(body: CreateDamagePackageRequest): Observable<DamagePackageModel> {
    this.isLoading.set(true);
    return this.http.post<DamagePackageModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании пакета:', error);
        this.translate.get('Ошибка при создании пакета').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  createWithTypes(body: CreateDamagePackageWithTypesRequest): Observable<DamagePackageModel> {
    this.isLoading.set(true);
    return this.http.post<DamagePackageModel>(`${this.baseUrl}/with-types`, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании пакета с типами повреждений:', error);
        this.translate.get('Ошибка при создании пакета').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(id: string, body: CreateDamagePackageRequest): Observable<DamagePackageModel> {
    this.isLoading.set(true);
    return this.http.put<DamagePackageModel>(`${this.baseUrl}/${id}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении пакета:', error);
        this.translate.get('Ошибка при обновлении пакета').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении пакета:', error);
        this.translate.get('Ошибка при удалении пакета').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  activate(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.patch<void>(`${this.baseUrl}/${id}/activate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при активации пакета:', error);
        this.translate.get('Ошибка при активации пакета').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deactivate(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.patch<void>(`${this.baseUrl}/${id}/deactivate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при деактивации пакета:', error);
        this.translate.get('Ошибка при деактивации пакета').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }
}
